import React, { useState, useEffect } from "react";
import { Upload, Button, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Location from "./Location";
import {
  fetchAsyncCarLeasingStepData,
  fetchAsynUserIdentification,
  fetchAsynUserinformation,
  getCarLeasingStepData,
  getUserIdentification,
  getUserInformation,
} from "../../redux/transactions/UntappedSlice";
import { useDispatch, useSelector } from "react-redux";

const GuarantorForm = ({ data, bikeType, onChange }) => {
  const dispatch = useDispatch();

  const initialGuarantors = {
    insurer_one: {
      phoneNumber: "",
      firstName: "",
      middleName: "",
      lastName: "",
      nationalId: "",
      occupation: "",
      province: "",
      district: "",
      sector: "",
      cell: "",
      village: "",
    },
    insurer_two: {
      phoneNumber: "",
      firstName: "",
      middleName: "",
      lastName: "",
      nationalId: "",
      occupation: "",
      province: "",
      district: "",
      sector: "",
      cell: "",
      village: "",
    },
  };

  const [guarantorsData, setGuarantorsData] = useState(initialGuarantors);
  const isElectricBike = bikeType === "ELECTRIC";

  const userInformation = useSelector(getUserInformation);
  const userIdentification = useSelector(getUserIdentification);
  const phoneNumber = useSelector((state) => state.untapped.phoneNumber);
  const currentStepSavedData = useSelector(getCarLeasingStepData);

  const [uploadedIDFile, setuploadedIDFile] = useState("");

  // ── Fetch step data ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (phoneNumber?.trim().length === 10) {
      dispatch(fetchAsyncCarLeasingStepData({ phone: phoneNumber.trim(), step: 5 }));
    }
  }, [dispatch, phoneNumber]);

  // ── Hydrate from saved step data ─────────────────────────────────────────────
  useEffect(() => {
    if (!currentStepSavedData?.step_data?.insurer_one) return;

    const insurerOne = currentStepSavedData.step_data.insurer_one;
    const residence = insurerOne.residence || {};
    const village = residence.village || {};
    const cells = village.cells || {};
    const sector = cells.sector || {};
    const district = sector.district || {};
    const province = district.province || {};

    setGuarantorsData((prev) => ({
      ...prev,
      insurer_one: {
        ...prev.insurer_one,
        phoneNumber:  insurerOne.phone       || "",
        firstName:    insurerOne.first_name  || "",
        lastName:     insurerOne.last_name   || "",
        nationalId:   insurerOne.identity    || "",
        occupation:   insurerOne.occupation  || "",
        gender:       insurerOne.gender      || "",
        province:     province.id            || "",
        provinceName: province.name          || "",
        district:     district.id            || "",
        districtName: district.name          || "",
        sector:       sector.id              || "",
        sectorName:   sector.name            || "",
        cell:         cells.id               || "",
        cellName:     cells.name             || "",
        village:      village.id             || "",
        villageName:  village.name           || "",
      },
    }));
  }, [currentStepSavedData]);

  // ── Auto-fill names from phone lookup ────────────────────────────────────────
  useEffect(() => {
    if (userInformation && userIdentification) {
      setGuarantorsData((prev) => ({
        ...prev,
        insurer_one: {
          ...prev.insurer_one,
          firstName:  userInformation?.firstname  ?? "",
          middleName: userInformation?.middleName ?? "",
          lastName:   userInformation?.lastname   ?? "",
          nationalId: userIdentification?.nid     ?? "",
        },
      }));
    }
  }, [userInformation, userIdentification]);

  // ── Trigger lookup when insurer_one phone reaches 10 digits ──────────────────
  useEffect(() => {
    const phone = guarantorsData?.insurer_one?.phoneNumber;
    if (phone?.length === 10) {
      dispatch(fetchAsynUserIdentification({ contact: phone }));
      dispatch(fetchAsynUserinformation({ contact: phone }));
    }
  }, [dispatch, guarantorsData?.insurer_one?.phoneNumber]);

  // ── Sync incoming `data` prop ─────────────────────────────────────────────────
  useEffect(() => {
    if (data) {
      setGuarantorsData({
        insurer_one: { ...data.insurer_one },
        insurer_two: { ...data.insurer_two },
      });
    }
  }, [data]);

  // ── Notify parent (debounced) ─────────────────────────────────────────────────
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (JSON.stringify(guarantorsData) !== JSON.stringify(data)) {
        onChange({ guarantorsData, uploadedIDFile });
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [guarantorsData, data, onChange, uploadedIDFile]);

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleInputChange = (insurerType, e) => {
    const { name, value } = e.target;
    setGuarantorsData((prev) => ({
      ...prev,
      [insurerType]: { ...prev[insurerType], [name]: value },
    }));
  };

  const validateFile = (file) => {
    const isPDF = file.type === "application/pdf";
    const isSmallEnough = file.size / 1024 / 1024 < 1;
    if (!isPDF) message.error(`${file.name} is not a PDF file!`);
    if (!isSmallEnough) message.error(`${file.name} exceeds the 1MB size limit!`);
    return isPDF && isSmallEnough;
  };

  const convertFileToBase64 = (file, callback) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => callback(reader.result);
    reader.onerror = (error) => console.error("Error converting file:", error);
  };

  const handleIdFileChange = (insurerType, info) => {
    const { file } = info;

    if (file.status !== "removed") {
      setuploadedIDFile(file);
      convertFileToBase64(file.originFileObj || file, (base64File) => {
        localStorage.setItem(`${insurerType}_uploadedIDFile`, base64File);
        setGuarantorsData((prev) => ({
          ...prev,
          [insurerType]: { ...prev[insurerType], uploadedIDFile: file.originFileObj || file },
        }));
        message.success(`${file.name} has been added successfully.`);
      });
    } else {
      setuploadedIDFile("");
      localStorage.removeItem(`${insurerType}_uploadedIDFile`);
      setGuarantorsData((prev) => ({
        ...prev,
        [insurerType]: { ...prev[insurerType], uploadedIDFile: null },
      }));
    }
  };

  const updatedLocation = (insurerType, location) => {
    setGuarantorsData((prev) => ({
      ...prev,
      [insurerType]: { ...prev[insurerType], ...location },
    }));
  };

  // ── Render single guarantor form ──────────────────────────────────────────────
  const renderGuarantorForm = (insurerType, title) => {
    const guarantorData = guarantorsData[insurerType];
    const idFileList = guarantorData.uploadedIDFile ? [guarantorData.uploadedIDFile] : [];

    return (
      <div className="rounded-lg p-4 mb-4">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
 {/* Names */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          {["firstName", "middleName", "lastName"].map((field) => (
            <div key={field}>
              <label className="block text-sm font-medium">
                {field === "firstName"
                  ? "Izina ribanza"
                  : field === "middleName"
                  ? "Izina ryo hagati"
                  : "Irindi zina"}
              </label>
              <input
                name={field}
                type="text"
                placeholder={
                  field === "firstName"
                    ? "Izina ribanza"
                    : field === "middleName"
                    ? "Izina ryo hagati"
                    : "Irindi zina"
                }
                value={guarantorData[field]}
                onChange={(e) => handleInputChange(insurerType, e)}
                className="w-full px-3 py-2 focus:outline-none border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
              />
            </div>
          ))}
        </div>
        {/* Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">Nimero ya telefone</label>
            <input
              name="phoneNumber"
              type="text"
              placeholder="078..."
              value={guarantorData.phoneNumber}
              onChange={(e) => handleInputChange(insurerType, e)}
              className="w-full px-3 py-2 focus:outline-none border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
            />
          </div>
          {/* National ID upload */}
        <div className="w-full border flex flex-col items-center justify-between mt-3 rounded-md pb-2 ">
          <label className="block text-sm font-medium mx-3">Injiza Indangamuntu</label>
          <Upload
            fileList={idFileList}
            customRequest={({ onSuccess }) => onSuccess("ok")}
            beforeUpload={(file) => {
              if (!validateFile(file)) return Upload.LIST_IGNORE;
              return false;
            }}
            onChange={(info) => handleIdFileChange(insurerType, info)}
          >
            <Button className="" icon={<UploadOutlined />}>
              Choose File
            </Button>
          </Upload>
        </div>
        </div>

        

       

        {/* National ID number + Occupation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium">Nimero y'irangamuntu</label>
            <input
              name="nationalId"
              type="number"
              placeholder="Nimero y'irangamuntu"
              value={guarantorData.nationalId}
              onChange={(e) => handleInputChange(insurerType, e)}
              className="w-full px-3 py-2 focus:outline-none border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Icyo akora</label>
            <input
              name="occupation"
              type="text"
              placeholder="Icyo akora"
              value={guarantorData.occupation}
              onChange={(e) => handleInputChange(insurerType, e)}
              className="w-full px-3 py-2 focus:outline-none border dark:border-gray-700 rounded-lg bg-gray-300 text-black"
            />
          </div>
        </div>

        {/* Location */}
        <div className="mt-4">
          <label className="block text-sm font-medium">Aho umwishingizi atuye</label>
          <Location
            province={guarantorData.province}
            district={guarantorData.district}
            sector={guarantorData.sector}
            cell={guarantorData.cell}
            village={guarantorData.village}
            onLocationChange={(location) => updatedLocation(insurerType, location)}
          />
        </div>
      </div>
    );
  };

  return (
    <form className="space-y-4">
      {renderGuarantorForm("insurer_one", "Umwishingizi wa mbere")}
      {/* {isElectricBike && renderGuarantorForm("insurer_two", "Umwishingizi wa kabiri")} */}
    </form>
  );
};

export default GuarantorForm;